import {Component, EventEmitter, inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {BaseComponent} from "../../../../BaseComponent";
import {DxFormComponent} from "devextreme-angular";
import {Device} from "../../../../../models/device/Device";
import {AcquisitionCronFrequencyEnum} from "../../../../../enums/registry/AcquisitionCronFrequencyEnum";
import {Gateway} from "../../../../../models/gateway/Gateway";
import {MetricGroup} from "../../../../../models/device-type/DeviceType";
import {DeviceTypeLite} from "../../../../../models/device-type/DeviceTypeLite";
import {ProtocolTypeEnum} from "../../../../../enums/registry/ProtocolTypeEnum";
import {GetDeviceTypeCB} from "../../../../../ope/device-type/GetDeviceTypeCB";
import {DeviceTypeOpeService} from "../../../../../services/ope/deviceTypeOpe.service";
import {DeviceDraft, MetricGroupInstanceDraft, MetricInstanceDraft} from "../../../../../models/device/DeviceDraft";
import * as uuid from "uuid";
import {getRowKey} from "../../../../../utils/rowKey";
import {MeasureGroupTableComponent} from "../measure-group-table/measure-group-table.component";
import {MeasureMetricTableComponent} from "../measure-metric-table/measure-metric-table.component";
import {RowStateTracker} from "../../../../../utils/RowStateTracker";

@Component({
  selector: 'app-device-create-form',
  templateUrl: './device-create-form.component.html',
  styleUrls: ['./device-create-form.component.css', '../../gateway-device.component.css', '../../../../common/drawer-style.css', '../../../base-component-with-drawer.component.css']
})
export class DeviceCreateFormComponent extends BaseComponent implements OnInit {

  @Output() selectedMetricGroupChange = new EventEmitter<MetricGroupInstanceDraft>()

  get selectedMetricGroup(): MetricGroupInstanceDraft | undefined {
    return this._selectedMetricGroup;
  }

  @Input() set selectedMetricGroup(value: MetricGroupInstanceDraft | undefined) {
    this._selectedMetricGroup = value;
    this.selectedMetricGroupChange.emit(value);
  }

  @ViewChild('newDeviceForm', {static: false}) newDeviceForm!: DxFormComponent;
  @ViewChild('groupMetricDataGrid', {static: false}) groupMetricDataGrid?: MeasureGroupTableComponent;
  @ViewChild('metricDataGrid', {static: false}) metricDataGrid?: MeasureMetricTableComponent;

  @Output() addDeviceChange = new EventEmitter<boolean>();

  get addDevice(): boolean {
    return this._addDevice;
  }

  @Input() set addDevice(value: boolean) {
    if (value && !this._addDevice) this.metricRowStateTracker.resetAll();
    this._addDevice = value;
    this.addDeviceChange.emit(value);
  }

  private _addDevice: boolean;

  @Input() newDevice?: DeviceDraft;
  @Input() selectedGateway?: Gateway;
  @Input() selectedRowMetricGroup: any;
  private _selectedMetricGroup?: MetricGroupInstanceDraft;
  @Input() deviceTypes: DeviceTypeLite[] = [];

  @Output() isNewFormValid = new EventEmitter<boolean>();
  @Output() putDevice: EventEmitter<DeviceDraft> = new EventEmitter<DeviceDraft>();

  deviceTypeOpeService: DeviceTypeOpeService = inject(DeviceTypeOpeService);
  protected acquisitionCronFrequencyValues: string[];
  /**
   * Id of the selected row in metric group data grid
   */

  selectedTab = 0;

  constructor() {
    super();
    this._addDevice = false;
    this.newDevice = new DeviceDraft();
    this.acquisitionCronFrequencyValues = Object.values(AcquisitionCronFrequencyEnum);
  }

  /**
   * Text box options for gateway fields
   */
  deviceNewTextOptions = {
    valueChangeEvent: 'keyup'
  };
  deviceNewNumberOptions: any = {
    valueChangeEvent: 'keyup',
    mode: 'number',
    value: 0
  };


  // Metrics-tab grids (groups + metrics); only the metrics one needs a selected group to exist.
  private get metricTables(): (MeasureGroupTableComponent | MeasureMetricTableComponent)[] {
    return [this.groupMetricDataGrid, this.metricDataGrid]
      .filter((t): t is MeasureGroupTableComponent | MeasureMetricTableComponent => !!t);
  }

  readonly metricRowStateTracker = new RowStateTracker();
  protected readonly getRowKey = getRowKey;

  focusActiveMetricEdit(): void {
    this.metricDataGrid?.focusActiveEdit();
  }

  // The metric tracker is read from this form, not from the metric table: the table is destroyed
  // whenever no group is selected, and the tracker keeps every group's rows through that.
  get rowStateTrackers(): RowStateTracker[] {
    return [this.groupMetricDataGrid?.rowStateTracker, this.metricRowStateTracker]
      .filter((t): t is RowStateTracker => !!t);
  }

  /**
   * Call putDevice with newly created gateway-device
   */
  async createNewDevice() {

    // A row left "in modifica" in the metrics tab was never confirmed into the bound data and
    // would be dropped silently by the save - warn before that happens. Validation runs after
    // the cancel, since discarding that row can be what makes the form valid again.
    if (this.rowStateTrackers.some(t => t.hasEditing())) {
      const proceed = await this.showSaveWithPendingEditDialogWithPromise();
      if (!proceed) return;
      this.metricTables.forEach(t => t.cancelActiveEdit());
    }

    if (this.checkNewDeviceValidation()) {
      if (this.newDevice!.protocolType === "MODBUS_RTU") {
        this.newDevice!.transportConfiguration!.dataBits = Number(this.newDevice!.transportConfiguration?.combinedField?.charAt(0));
        this.newDevice!.transportConfiguration!.parity = this.newDevice!.transportConfiguration?.combinedField?.charAt(1)!;
        this.newDevice!.transportConfiguration!.stopBits = Number(this.newDevice!.transportConfiguration?.combinedField?.charAt(2));
      }

      this.putDevice.emit(this.newDevice!);
    }

  }

  validateDeviceName(event: any) {
    return /^[a-z0-9\s_-]+$/i.test(event.value);
  }

  getAcquisitionCronLabel = (acquisitionCron: string) => {
    return this.translate.instant('FREQUENCY.' + acquisitionCron);
  };

  onGroupsChanged() {
    if (this.selectedMetricGroup && !this.newDevice?.metricGroupInstances?.some(g => getRowKey(g) === getRowKey(this.selectedMetricGroup)))
      this.selectedMetricGroup = undefined;
    this.metricDataGrid?.refresh();
    this.checkNewDeviceValidation();
  }

  /**
   * Check if new gateway is valid
   */
  checkNewDeviceValidation(): boolean {

    const result = this.newDeviceForm.instance.validate();

    this.isNewFormValid.emit(result.isValid);

    return !!result.isValid;
  }

  getNewDeviceType = (e: any) => {

    let device: DeviceDraft;

    if (this.addDevice) {
      device = this.newDevice!;
      device.transportConfiguration = {};
      this.selectedRowMetricGroup = [];
      this._selectedMetricGroup = undefined;
      this.metricRowStateTracker.resetAll();
      this.getDeviceType();
    }
  };

  /**
   * Get all gateway-device types
   */
  getDeviceType() {
    const cb = new GetDeviceTypeCB();

    let device = this.appService.deepCopy(this.newDevice!);

    cb.i.id = device.typeId;

    this.deviceTypeOpeService.getDeviceType(cb).then((_) => {
      this.instantiateDevice(device, cb);
      switch (device.protocolType) {
        case ProtocolTypeEnum.MODBUS_TCP: {
          device.transportConfiguration.node = 0;
          device.transportConfiguration.port = 502;
          device.transportConfiguration.ipAddress = "127.0.0.1";
          device.transportConfiguration.timeout = 3000;
          device.transportConfiguration.protocolType = ProtocolTypeEnum.MODBUS_TCP;
          break;
        }
        case ProtocolTypeEnum.MODBUS_RTU: {
          device.transportConfiguration.baudRate = 9600;
          device.transportConfiguration.deviceAddr = 0;
          device.transportConfiguration.stopBits = 1;
          device.transportConfiguration.parity = "N";
          device.transportConfiguration.dataBits = 8;
          device.transportConfiguration.combinedField = "8N1";
          if (this.selectedGateway?.type?.additionalInfo?.rtuPorts?.length === 1)
            device.transportConfiguration.portName = this.selectedGateway?.type?.additionalInfo?.rtuPorts[0];
          device.transportConfiguration.timeout = 3000;
          device.transportConfiguration.protocolType = ProtocolTypeEnum.MODBUS_RTU;
          break;
        }
        case ProtocolTypeEnum.SIEMENS_S7: {
          device.transportConfiguration.rack = 0;
          device.transportConfiguration.slot = 1;
          device.transportConfiguration.ipAddress = "127.0.0.1";
          device.transportConfiguration.port = 102;
          device.transportConfiguration.timeout = 3000;
          device.transportConfiguration.protocolType = ProtocolTypeEnum.SIEMENS_S7;
          break;
        }
        default:
          break;
      }
      this.newDevice = this.appService.deepCopy(device);
      setTimeout(() => {
        this.newDevice = this.appService.deepCopy(device);
      }, 0);
    });
  }

  private instantiateDevice(device: DeviceDraft, deviceTypeCB: GetDeviceTypeCB) {

    const deviceType = deviceTypeCB.o.deviceType;

    device.typeId = deviceType.id;
    device.modelName = deviceType.modelName;
    device.partNumber = deviceType.partNumber;
    device.modbusWritePauseMillis = deviceType.modbusWritePauseMillis;
    device.manufacturerId = deviceType.manufacturer?.id;
    device.manufacturer = deviceType.manufacturer;
    device.protocolType = deviceType.protocolType;

    device.metricGroupInstances = [];

    for (const metricGroup of deviceType.metricGroups ?? []) {
      const metricGroupInstance = new MetricGroupInstanceDraft();
      metricGroupInstance.metricGroupId = metricGroup.id!;
      metricGroupInstance.name = metricGroup.name;
      metricGroupInstance.metricOffset = 0;
      metricGroupInstance.transportConfiguration = metricGroup.transportConfiguration;
      metricGroupInstance.isWritableOnEdge = metricGroup.isWritableOnEdge ?? false;
      // None of these rows have a real `id` yet (backend assigns one on save) - the
      // create-form's grids key off `id`, so give each row a unique client-side key to
      // avoid every row colliding on the same `undefined` key.
      metricGroupInstance._uiKey = uuid.v4();

      for (const metric of metricGroup.metrics ?? []) {
        const metricInstance = new MetricInstanceDraft();
        metricInstance.metricId = metric.id!;
        metricInstance.selected = metric.selected ?? true;
        metricInstance.name = metric.name;
        metricInstance.divider = metric.divider;
        metricInstance.decimalDigits = metric.decimalDigits;
        metricInstance.transportConfiguration = metric.transportConfiguration;
        metricInstance.dataUnitId = metric.dataUnit?.id;
        metricInstance._uiKey = uuid.v4();
        metricGroupInstance.metrics.push(metricInstance);
      }

      device.metricGroupInstances.push(metricGroupInstance);
    }
  }
}
